import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArchiveBoxIcon, ArrowPathIcon, CheckIcon, ClockIcon, MagnifyingGlassIcon, Square3Stack3DIcon,
} from '@heroicons/react/24/outline';
import {
  runnerApi, errorText, type CleanupOverview, type MarkOutcome, type MarkPair, type MarkResult, type TemplateDiff,
} from '../../../../api/migrationRunner';
import { Alert, Btn, ErrorBox, Spinner, Toast, type ToastMsg } from '../ui';
import { FolderLintPanel } from '../HealthTab';
import type { RunnerNav } from '../nav';
import { BigCard, TONE_CLS, plural, skippedText, type CleanupCtx, type Screen } from './shared';
import TemplateView from './TemplateView';
import PendingView from './PendingView';
import StuckView from './StuckView';
import FreshView from './FreshView';

const SCREENS: { key: Screen; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'template', label: 'Template' },
  { key: 'pending', label: 'Pending' },
  { key: 'stuck', label: 'Stuck' },
  { key: 'fresh', label: 'Start fresh' },
];

/**
 * Database › Clean up (approved mockup "Database Clean Up", 2026-10-18): one
 * place for everything that keeps showing up — template differences grouped
 * by item, pending migrations sorted by a dry-run scan, runs that never
 * finished, and Start fresh. Every mark is recorded and undoable for 30 days.
 */
const CleanupTab: React.FC<{
  nav: RunnerNav; isOwner: boolean; refreshKey: number; onBadge?: (n: number) => void;
}> = ({ nav, isOwner, refreshKey, onBadge }) => {
  const [screen, setScreen] = useState<Screen>('overview');
  const [overview, setOverview] = useState<CleanupOverview | null>(null);
  const [template, setTemplate] = useState<TemplateDiff | null>(null);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<ToastMsg | null>(null);
  const [tick, setTick] = useState(0);
  const [settingUp, setSettingUp] = useState(false);

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const o = await runnerApi.cleanupOverview();
      setOverview(o);
      onBadge?.(o.counts.badge);
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not load Clean up'));
    } finally {
      setLoading(false);
    }
  }, [onBadge]);

  const loadTemplate = useCallback(async () => {
    try {
      setTemplate(await runnerApi.templateDiff());
      setTemplateError(null);
    } catch (err) {
      setTemplateError(errorText(err, 'Template check failed'));
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview, refreshKey, tick]);
  useEffect(() => { loadTemplate(); }, [loadTemplate, refreshKey]);

  const notify = useCallback((text: string, undo?: () => Promise<void> | void) => {
    setToast({ id: Date.now(), text, undo });
  }, []);
  const changed = useCallback(() => setTick((t) => t + 1), []);
  const closeToast = useCallback(() => setToast(null), []);

  const mark = useCallback(async (items: MarkPair[], outcome: MarkOutcome, note: string, done: (r: MarkResult) => string) => {
    let r: MarkResult;
    try {
      r = await runnerApi.mark(items, outcome, note);
    } catch (err) {
      throw new Error(errorText(err, 'Could not mark'));
    }
    const markId = r.markId;
    notify(r.marked ? `${done(r)}${skippedText(r)}` : `Nothing marked.${skippedText(r)}`, markId ? async () => {
      try {
        await runnerApi.undoMark(markId);
        notify('Undone.');
      } catch (err) {
        notify(errorText(err, 'Undo failed'));
      }
      changed();
    } : undefined);
    changed();
    return r;
  }, [notify, changed]);

  const ctx: CleanupCtx = useMemo(() => ({
    nav, isOwner, ready: Boolean(overview?.schema.ready), notify, mark, go: setScreen, changed,
  }), [nav, isOwner, overview?.schema.ready, notify, mark, changed]);

  const setUp = async () => {
    setSettingUp(true);
    try {
      const r = await runnerApi.cleanupBootstrap();
      const bad = r.results.find((x) => x.status !== 'success' && x.status !== 'skipped');
      notify(bad ? `Set up stopped at ${bad.migration}: ${bad.error || bad.status}` : 'Clean up is set up.');
      changed();
    } catch (err) {
      notify(errorText(err, 'Set up failed'));
    } finally {
      setSettingUp(false);
    }
  };

  const templateCount = template ? template.counts.template_behind + template.counts.company_behind + template.counts.leftover : null;
  const scan = overview?.pendingScan;
  const scanned = scan?.status === 'done' && scan.summary;

  return (
    <div className="space-y-4 px-6 py-6">
      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Clean up screens">
        {SCREENS.map((s) => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={screen === s.key}
            onClick={() => setScreen(s.key)}
            className={`rounded-full border px-3 py-1 text-[12.5px] font-semibold ${screen === s.key ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}
          >
            {s.label}
            {s.key === 'stuck' && overview?.counts.stuck ? <span className="ml-1.5 text-red-500">{overview.counts.stuck}</span> : null}
          </button>
        ))}
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}
      {overview && !overview.schema.ready && (
        <Alert
          tone="warn"
          title="Clean up isn't set up yet"
          action={isOwner && <Btn onClick={setUp} disabled={settingUp || !overview.runnerReady}>{settingUp ? 'Setting up…' : 'Set up Clean up'}</Btn>}
        >
          {overview.runnerReady
            ? 'It needs the registry migration migrations-2026-10-18-db-cleanup (marks with undo, scans, the ignore list). You can look around; marking, scanning and ignoring wait for it.'
            : 'Set up the runner first (the banner on the Status tab), then Clean up.'}
        </Alert>
      )}

      {screen === 'overview' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-[15px] font-bold text-slate-900">What needs sorting out</h2>
              <p className="text-[12.5px] text-slate-500">One list for everything that keeps showing up. Nothing runs until you press a button and confirm.</p>
            </div>
            <Btn kind="ghost" small onClick={() => { changed(); loadTemplate(); }} disabled={loading}>
              {loading ? <Spinner className="h-3.5 w-3.5" /> : <ArrowPathIcon className="h-4 w-4" />} Re-check all
            </Btn>
          </div>
          <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
            <BigCard
              icon={<Square3Stack3DIcon className="h-5 w-5" />}
              count={templateCount ?? (templateError ? '?' : <Spinner className="h-6 w-6" />)}
              title="Template differences"
              body={templateError
                ? templateError
                : templateCount === null
                  ? "Reading every company's tables…"
                  : templateCount
                    ? `Companies and the template disagree about ${plural(templateCount, 'thing')}. Make them all match, or say a difference is fine.`
                    : 'Every company matches the template.'}
              tone={templateCount ? TONE_CLS.warn : TONE_CLS.ok}
              label={templateCount ? 'needs a decision' : templateCount === 0 ? 'all match' : 'checking'}
              onOpen={() => setScreen('template')}
            />
            <BigCard
              icon={<MagnifyingGlassIcon className="h-5 w-5" />}
              count={overview ? overview.counts.pending.toLocaleString() : <Spinner className="h-6 w-6" />}
              title="Pending migrations"
              body={scanned
                ? `Scanned: ${scan.summary!.counts.inplace} already in place, ${scan.summary!.counts.needed} still needed.`
                : 'Never run on some databases. Scan to see which are really still needed.'}
              tone={overview?.counts.pending ? TONE_CLS.warn : TONE_CLS.ok}
              label={scan?.status === 'running' ? 'scanning' : scanned ? 'scanned' : 'not scanned yet'}
              onOpen={() => setScreen('pending')}
            />
            <BigCard
              icon={<ClockIcon className="h-5 w-5" />}
              count={overview ? overview.counts.stuck : <Spinner className="h-6 w-6" />}
              title="Stuck or can't finish"
              body="Runs that never finished, or keep getting skipped or locked. Every one can now be closed out."
              tone={overview?.counts.stuck ? TONE_CLS.bad : TONE_CLS.ok}
              label={overview?.counts.stuck ? 'stuck' : 'none'}
              onOpen={() => setScreen('stuck')}
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
            <div className="min-w-0 flex-1">
              <b className="text-slate-900">Start fresh</b>
              <div className="text-[12.5px] text-slate-500">Lots of old migrations you already applied still show as pending (registry and companies). Clear them all in one go, checked first.</div>
            </div>
            <Btn onClick={() => setScreen('fresh')}>Start fresh</Btn>
          </div>
          {overview && (
            <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-[13px] text-slate-700">
              <ArchiveBoxIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
              <span>
                {overview.archiveOlderThanDays
                  ? <>Folders older than <b>{overview.archiveOlderThanDays} days</b> are archived automatically, so they leave every list here. Change it on the Settings tab; un-archive one folder from Migration files &rsaquo; View archived.</>
                  : <>Archiving old folders automatically is off. Turn it on (for example 30 days) on the Settings tab.</>}
              </span>
            </div>
          )}
          <div className="flex items-start gap-2 rounded-xl bg-blue-50 px-4 py-3 text-[13px] text-slate-700">
            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <span><b>Every mark is recorded</b> with your name, a note and the reason, and shows in History. Any mark can be undone for 30 days. Marking something done never changes a company's database; only Apply and Run do.</span>
          </div>
          <FolderLintPanel refreshKey={refreshKey + tick} />
        </div>
      )}

      {screen === 'template' && <TemplateView ctx={ctx} diff={template} error={templateError} reload={loadTemplate} refreshKey={refreshKey} />}
      {screen === 'pending' && <PendingView ctx={ctx} refreshKey={refreshKey + tick} />}
      {screen === 'stuck' && <StuckView ctx={ctx} refreshKey={refreshKey + tick} />}
      {screen === 'fresh' && <FreshView ctx={ctx} pendingBefore={overview?.counts.pending ?? null} />}

      <Toast toast={toast} onClose={closeToast} />
    </div>
  );
};

export default CleanupTab;
